import { Grid, Tooltip } from '@material-ui/core';
import { CourierType } from 'const/CourierType';
import { PartitionType } from 'const/PartitionType';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import { PartitionCard } from '../PartitionCard/PartitionCard';
import { Section } from '../Section/section';
import { CourierChip } from '../CourierChip/CourierChip';
import { AreaRating } from '../../../const/AreaRating';

// TODO: Add more colors. We should at least have 20. (20 partitions * 500 orders = 10000 orders)
/**
 * Notes :
 * Keep in sync with list in `dispatch-frontend/src/components/MapBox/ZonesOverlay/PartitionsAreaLayers.ts`
 * Colors are taken from MUI colors https://mui.com/material-ui/customization/color/
 * Color used should be other then RED and GREEN (include the pinkish red and light/dark green)
 */
const colors = [
  '#ab47bc', // purple 400
  '#42a5f5', // blue 400
  '#ffa726', // orange 400
  '#8d6e63', // brown 400
  '#ff7043', // deep-orange 400
  '#5c6bc0', // indigo 400
  '#26c6da', // cyan 400
  '#ffca28', // amber 400
  '#9c27b0', // purple 500
  '#2196f3', // blue 500
  '#ff9800', // orange 500
  '#795548', // brown 500
  '#ff5722', // deep-orange 500
  '#3f51b5', // indigo 500
  '#00bcd4', // cyan 500
  '#ffc107', // amber 500
];

interface PartitionListProps {
  data: Array<PartitionType>;
  zonePartitionsCount: Record<string, number> | undefined;
  activePartition: PartitionType;
  couriers: Array<CourierType>;
  onSelectPartition?: (partition: PartitionType) => void;
  addCourierToPartition?: (
    courier: CourierType,
    partition: PartitionType
  ) => void;
  addCourierToActivePartition?: (courier: CourierType) => void;
  removeCourierFromPartition?: (courier: CourierType) => void;
  onHoverCourier: (couriers?: CourierType[], ratingType?: AreaRating) => void;
}

export const PartitionList = ({
  data,
  zonePartitionsCount,
  activePartition,
  couriers,
  onSelectPartition,
  addCourierToPartition,
  addCourierToActivePartition,
  removeCourierFromPartition,
  onHoverCourier,
  ...props
}: PartitionListProps) => {
  const [unusedCouriers, setUnusedCouriers] = useState<Array<CourierType>>([]);

  /* Checking for the Unused Couriers */
  useEffect(() => {
    const newUsedCouriers = data.reduce(
      (acc, partition) => acc.concat(partition.couriers),
      []
    );

    const newUnusedCouriers = couriers.filter(
      courier => !newUsedCouriers.includes(parseInt(courier.id))
    );
    setUnusedCouriers(newUnusedCouriers);
  }, [data, couriers]);

  return (
    <Section>
      <h2>
        <span>Partitions ({data.length})</span>
        <Tooltip title="Select one of the Partition, then choose the Zones and Couriers">
          <span className="tooltip icon">
            <FaExclamationCircle size={16} color="grey" />
          </span>
        </Tooltip>
      </h2>
      <p>Selected zones will be added to current Active partition.</p>

      {data.map((partition, partitionIndex) => (
        <PartitionCard
          key={`partition-${partition.id || uniqueId()}`}
          partition={partition}
          zonePartitionsCount={zonePartitionsCount}
          active={partition.id === activePartition?.id}
          unusedCouriers={unusedCouriers}
          assignedCouriers={couriers.filter(
            courier => partition.couriers.indexOf(parseInt(courier.id)) >= 0
          )}
          color={colors[partitionIndex]}
          onSelectPartition={onSelectPartition}
          addCourierToPartition={addCourierToPartition}
          addCourierToActivePartition={addCourierToActivePartition}
          removeCourierFromPartition={removeCourierFromPartition}
          onCourierHoverChange={onHoverCourier}
        />
      ))}

      <h3>Unused couriers</h3>
      {unusedCouriers.length === 0 && <p>None</p>}
      <Grid container spacing={1}>
        {unusedCouriers.map(courier => (
          <Grid item key={`unassigned-courier-${courier?.id || uniqueId()}`}>
            <CourierChip
              courier={courier}
              onReassignToActivePartition={addCourierToActivePartition}
              onCourierHoverChange={onHoverCourier}
            />
          </Grid>
        ))}
      </Grid>
    </Section>
  );
};
